
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MembershipApplication } from "@/components/admin/membership/types";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";

interface ApplicationDetailDialogProps {
  application: MembershipApplication | null;
  onOpenChange: (open: boolean) => void;
  onStatusChange?: (id: string, status: string) => Promise<void>;
}

const ApplicationDetailDialog = ({ 
  application, 
  onOpenChange,
  onStatusChange 
}: ApplicationDetailDialogProps) => {
  const [status, setStatus] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Reset status whenever application changes
  useEffect(() => {
    if (application) {
      setStatus(application.status);
    }
  }, [application]);

  if (!application) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const handleStatusChange = async () => {
    if (!application || !status || !onStatusChange || status === application.status || isUpdating) return;
    
    setIsUpdating(true);
    try {
      await onStatusChange(application.id, status);
    } catch (error) {
      console.error("Error updating status:", error);
      // Reset to the original status on error
      setStatus(application.status);
    } finally {
      setTimeout(() => setIsUpdating(false), 500); // Small delay to prevent rapid clicks
    }
  };

  return (
    <Dialog open={!!application} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Membership Application Details</DialogTitle>
          <DialogDescription>
            Review and manage application details below
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-6 py-4">
          <div>
            <h3 className="font-semibold mb-4 text-lg">Personal Information</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Name:</span> {application.first_name} {application.last_name}</p>
              <p><span className="font-medium">Email:</span> {application.email}</p>
              <p><span className="font-medium">Mobile:</span> {application.mobile_area_code} {application.mobile_number}</p>
              
              <div className="mt-4">
                <p className="font-medium">Home Address:</p>
                <p>{application.home_address_street1}</p>
                {application.home_address_street2 && (
                  <p>{application.home_address_street2}</p>
                )}
                <p>{application.home_address_city}</p>
                <p>{application.home_address_postal_code}</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-lg">Business Information</h3>
            <div className="space-y-2">
              <p><span className="font-medium">Business Name:</span> {application.business_name}</p>
              <p><span className="font-medium">Phone:</span> {application.business_area_code} {application.business_number}</p>
              
              <div className="mt-4">
                <p className="font-medium">Business Address:</p>
                <p>{application.business_address_street1}</p>
                {application.business_address_street2 && (
                  <p>{application.business_address_street2}</p>
                )}
                <p>{application.business_address_city}</p>
                <p>{application.business_address_postal_code}</p>
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <h3 className="font-semibold mb-4 text-lg">Membership Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p><span className="font-medium">Membership Type:</span> {application.membership_type}</p>
                <p><span className="font-medium">Application Status:</span> 
                  <Badge 
                    variant="secondary"
                    className={`${getStatusColor(application.status)} text-white ml-2`}
                  >
                    {application.status}
                  </Badge>
                </p>
              </div>
              <div>
                <p><span className="font-medium">Current Potato Area:</span> {application.current_potato_area || "N/A"}</p>
                <p><span className="font-medium">Total Potato Area:</span> {application.total_potato_area || "N/A"}</p>
                <p><span className="font-medium">Planned Potato Area:</span> {application.planned_potato_area || "N/A"}</p>
              </div>
            </div>

            {application.signature && (
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Signature</h3>
                <img 
                  src={application.signature} 
                  alt="Applicant's signature" 
                  className="max-w-[300px] border rounded p-2"
                />
              </div>
            )}

            {onStatusChange && (
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-4">Update Application Status</h3>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-2">Select New Status</p>
                      <Select
                        value={status}
                        onValueChange={setStatus}
                        disabled={isUpdating}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-900 border border-border shadow-md z-50 absolute">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleStatusChange}
                      disabled={!status || isUpdating || status === application.status}
                    >
                      {isUpdating ? "Updating..." : "Update Status"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationDetailDialog;
